import React, { useState } from 'react';
import { getTenant } from '../../../theme/tokens';
import { BRAND } from '../../../shared/config/brand';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, RefreshCwIcon, CheckCircleIcon, XCircleIcon, ClockIcon, AlertCircleIcon, UserIcon, CreditCardIcon, FileTextIcon, MessageSquareIcon } from 'lucide-react';
import { Refund, RefundStatus } from '../../../shared/types/Refund';
export function RefundDetail() {
  const {
    t
  } = useTranslation('refunds');
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalAction, setApprovalAction] = useState<'approve' | 'reject'>('approve');
  const [approvalNotes, setApprovalNotes] = useState('');
  // Mock data - in a real app, this would be fetched from an API
  const isAttentionItem = id === 'RF-2024-0045';
  const refund: Refund = isAttentionItem ? {
    id: 'RF-2024-0045',
    refundId: 'RF-2024-0045',
    transactionId: 'TXN-2026-5512',
    originalAmount: 45000,
    refundAmount: 45000,
    currency: getTenant().currency.code,
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested refund — order delivered late',
    requestedBy: 'Ahmad Rahman',
    requestedAt: '2026-05-04T09:00:00Z',
    customerName: 'Ahmad Rahman',
    customerEmail: 'ahmad.rahman@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4521',
    merchantName: BRAND.name,
    merchantId: 'MERCH-MY-001',
    notes: 'Flagged from Attention Needed — requires merchant approval within 1 day.',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2026-05-04T09:00:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'Ahmad Rahman'
    }, {
      id: '2',
      timestamp: '2026-05-04T09:05:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  } : {
    id: id || '1',
    refundId: 'REF-2024-001',
    transactionId: 'TXN-2024-1234',
    originalAmount: 125000,
    refundAmount: 125000,
    currency: getTenant().currency.code,
    status: 'pending_approval',
    reason: 'customer_request',
    reasonDescription: 'Customer requested cancellation due to order error',
    requestedBy: 'John Smith',
    requestedAt: '2024-01-18T10:30:00Z',
    customerName: 'John Smith',
    customerEmail: 'john.smith@email.com',
    paymentMethod: 'Visa',
    cardLast4: '4242',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    notes: 'Customer called support to request refund. Order was placed by mistake.',
    approvalWorkflow: [{
      id: '1',
      approverName: 'Sarah Manager',
      approverEmail: 'sarah.m@merchant.com',
      status: 'pending'
    }],
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-18T10:30:00Z',
      status: 'requested',
      description: 'Refund requested by customer',
      actor: 'John Smith'
    }, {
      id: '2',
      timestamp: '2024-01-18T10:35:00Z',
      status: 'pending_approval',
      description: 'Awaiting approval from merchant'
    }],
    isPartial: false
  };
  const getStatusBadge = (status: RefundStatus | string) => {
    const styles: Record<string, string> = {
      requested: 'bg-blue-100 text-blue-800',
      pending_approval: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      failed: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',
      pending: 'bg-yellow-100 text-yellow-800'
    };
    const labels: Record<string, string> = {
      requested: 'Requested',
      pending_approval: 'Pending Approval',
      approved: 'Approved',
      processing: 'Processing',
      completed: 'Completed',
      rejected: 'Rejected',
      failed: 'Failed',
      cancelled: 'Cancelled',
      pending: 'Pending'
    };
    return <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-9297">
        {labels[status] || status}
      </span>;
  };
  const handleApproval = () => {
    // In a real app, this would make an API call
    console.log(`${approvalAction} refund with notes:`, approvalNotes);
    setShowApprovalModal(false);
    // Navigate back or show success message
  };
  return <div className="space-y-6" data-id="element-9298">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-9299">
        <button onClick={() => navigate('/refunds')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-9300">
          <ArrowLeftIcon size={20} data-id="element-9301" />
          {t('pages.RefundDetail.back_to_refunds')}
        </button>
        {refund.status === 'pending_approval' && <div className="flex gap-2" data-id="element-9302">
            <button onClick={() => {
          setApprovalAction('reject');
          setShowApprovalModal(true);
        }} className="flex items-center gap-2 px-4 py-2 border border-red-300 text-red-700 rounded-lg hover:bg-red-50" data-id="element-9303">
              <XCircleIcon size={18} data-id="element-9304" />
              {t('pages.RefundDetail.reject')}
            </button>
            <button onClick={() => {
          setApprovalAction('approve');
          setShowApprovalModal(true);
        }} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700" data-id="element-9305">
              <CheckCircleIcon size={18} data-id="element-9306" />
              {t('pages.RefundDetail.approve')}
            </button>
          </div>}
      </div>
      {/* Refund Header */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9307">
        <div className="flex items-start justify-between mb-4" data-id="element-9308">
          <div data-id="element-9309">
            <div className="flex items-center gap-3 mb-2" data-id="element-9310">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-9311">
                {refund.refundId}
              </h1>
              {refund.isPartial && <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-sm font-medium rounded" data-id="element-9312">
                  {t('pages.RefundDetail.partial_refund')}
                </span>}
            </div>
            <div className="text-sm text-gray-500" data-id="element-9313">
              {t('pages.RefundDetail.transaction')} {refund.transactionId}
            </div>
          </div>
          {getStatusBadge(refund.status)}
        </div>
        {/* Key Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-9314">
          <div data-id="element-9315">
            <div className="text-sm text-gray-500 mb-1" data-id="element-9316">
              {t('pages.RefundDetail.refund_amount')}
            </div>
            <div className="text-2xl font-semibold text-gray-900" data-id="element-9317">
              {t('pages.RefundDetail.usd')}{' '}
              {refund.refundAmount.toLocaleString('en-US')}
            </div>
            {refund.isPartial && <div className="text-sm text-gray-500 mt-1" data-id="element-9318">
                {t('pages.RefundDetail.of_usd')}{' '}
                {refund.originalAmount.toLocaleString('en-US')}{' '}
                {t('pages.RefundDetail.original')}
              </div>}
          </div>
          <div data-id="element-9319">
            <div className="text-sm text-gray-500 mb-1" data-id="element-9320">
              {t('pages.RefundDetail.requested_by')}
            </div>
            <div className="text-lg font-medium text-gray-900" data-id="element-9321">
              {refund.requestedBy}
            </div>
            <div className="text-sm text-gray-500" data-id="element-9322">
              {new Date(refund.requestedAt).toLocaleString('en-GB')}
            </div>
          </div>
          <div data-id="element-9323">
            <div className="text-sm text-gray-500 mb-1" data-id="element-9324">
              {t('pages.RefundDetail.merchant')}
            </div>
            <div className="text-lg font-medium text-gray-900" data-id="element-9325">
              {refund.merchantName}
            </div>
            <div className="text-sm text-gray-500" data-id="element-9326">{refund.merchantId}</div>
          </div>
        </div>
      </div>
      {/* Customer & Payment Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-9327">
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9328">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9329">
            <UserIcon size={20} className="text-blue-600" data-id="element-9330" />
            {t('pages.RefundDetail.customer_information')}
          </h3>
          <div className="space-y-3" data-id="element-9331">
            <div data-id="element-9332">
              <div className="text-sm text-gray-500" data-id="element-9333">
                {t('pages.RefundDetail.name')}
              </div>
              <div className="text-base font-medium text-gray-900" data-id="element-9334">
                {refund.customerName}
              </div>
            </div>
            <div data-id="element-9335">
              <div className="text-sm text-gray-500" data-id="element-9336">
                {t('pages.RefundDetail.email')}
              </div>
              <div className="text-base font-medium text-gray-900" data-id="element-9337">
                {refund.customerEmail}
              </div>
            </div>
            <div data-id="element-9338">
              <div className="text-sm text-gray-500" data-id="element-9339">
                {t('pages.RefundDetail.payment_method')}
              </div>
              <div className="flex items-center gap-2" data-id="element-9340">
                <CreditCardIcon size={16} className="text-gray-400" data-id="element-9341" />
                <span className="text-base font-medium text-gray-900" data-id="element-9342">
                  {refund.paymentMethod}{' '}
                  {refund.cardLast4 && `•••• ${refund.cardLast4}`}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9343">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2" data-id="element-9344">
            <FileTextIcon size={20} className="text-blue-600" data-id="element-9345" />
            {t('pages.RefundDetail.refund_reason')}
          </h3>
          <div className="space-y-3" data-id="element-9346">
            <div data-id="element-9347">
              <div className="text-sm text-gray-500" data-id="element-9348">
                {t('pages.RefundDetail.category')}
              </div>
              <div className="text-base font-medium text-gray-900 capitalize" data-id="element-9349">
                {refund.reason.replace(/_/g, ' ')}
              </div>
            </div>
            {refund.reasonDescription && <div data-id="element-9350">
                <div className="text-sm text-gray-500" data-id="element-9351">
                  {t('pages.RefundDetail.description')}
                </div>
                <div className="text-base text-gray-900" data-id="element-9352">
                  {refund.reasonDescription}
                </div>
              </div>}
            {refund.notes && <div data-id="element-9353">
                <div className="text-sm text-gray-500" data-id="element-9354">
                  {t('pages.RefundDetail.additional_notes')}
                </div>
                <div className="text-base text-gray-900" data-id="element-9355">{refund.notes}</div>
              </div>}
          </div>
        </div>
      </div>
      {/* Approval Workflow */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9356">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-9357">
          {t('pages.RefundDetail.approval_workflow')}
        </h3>
        <div className="space-y-4" data-id="element-9358">
          {refund.approvalWorkflow.map((step, index) => <div key={step.id} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg" data-id="element-9359">
              <div className="flex-shrink-0" data-id="element-9360">
                {step.status === 'approved' ? <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" data-id="element-9361">
                    <CheckCircleIcon size={20} className="text-green-600" data-id="element-9362" />
                  </div> : step.status === 'rejected' ? <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center" data-id="element-9363">
                    <XCircleIcon size={20} className="text-red-600" data-id="element-9364" />
                  </div> : <div className="w-10 h-10 rounded-full bg-yellow-100 flex items-center justify-center" data-id="element-9365">
                    <ClockIcon size={20} className="text-yellow-600" data-id="element-9366" />
                  </div>}
              </div>
              <div className="flex-1" data-id="element-9367">
                <div className="flex items-center justify-between mb-2" data-id="element-9368">
                  <div data-id="element-9369">
                    <div className="text-sm font-medium text-gray-900" data-id="element-9370">
                      {step.approverName}
                    </div>
                    <div className="text-sm text-gray-500" data-id="element-9371">
                      {step.approverEmail}
                    </div>
                  </div>
                  {getStatusBadge(step.status)}
                </div>
                {step.timestamp && <div className="text-sm text-gray-500 mb-2" data-id="element-9372">
                    {new Date(step.timestamp).toLocaleString('en-GB')}
                  </div>}
                {step.notes && <div className="mt-2 p-3 bg-gray-50 rounded text-sm text-gray-700" data-id="element-9373">
                    {step.notes}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Lifecycle Timeline */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-9374">
        <h3 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-9375">
          {t('pages.RefundDetail.refund_timeline')}
        </h3>
        <div className="space-y-4" data-id="element-9376">
          {refund.lifecycle.map((event, index) => <div key={event.id} className="flex gap-4" data-id="element-9377">
              <div className="flex flex-col items-center" data-id="element-9378">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${index === refund.lifecycle.length - 1 ? 'bg-blue-100' : 'bg-gray-100'}`} data-id="element-9379">
                  {event.status === 'completed' ? <CheckCircleIcon size={16} className="text-green-600" data-id="element-9380" /> : event.status === 'rejected' || event.status === 'failed' ? <XCircleIcon size={16} className="text-red-600" data-id="element-9381" /> : <ClockIcon size={16} className={index === refund.lifecycle.length - 1 ? 'text-blue-600' : 'text-gray-400'} data-id="element-9382" />}
                </div>
                {index < refund.lifecycle.length - 1 && <div className="w-0.5 h-12 bg-gray-200 my-1" data-id="element-9383"></div>}
              </div>
              <div className="flex-1 pb-4" data-id="element-9384">
                <div className="text-sm font-medium text-gray-900" data-id="element-9385">
                  {event.description}
                </div>
                <div className="text-xs text-gray-500 mt-1" data-id="element-9386">
                  {new Date(event.timestamp).toLocaleString('en-GB')}
                </div>
                {event.actor && <div className="text-xs text-gray-500 mt-1" data-id="element-9387">
                    {t('pages.RefundDetail.by')} {event.actor}
                  </div>}
                {event.metadata && <div className="mt-2 p-2 bg-gray-50 rounded text-xs" data-id="element-9388">
                    {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-9389">
                        <span className="font-medium" data-id="element-9390">
                          {key.replace(/_/g, ' ')}:
                        </span>{' '}
                        {String(value)}
                      </div>)}
                  </div>}
              </div>
            </div>)}
        </div>
      </div>
      {/* Approval Modal */}
      {showApprovalModal && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-9391">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-9392">
            <div className="fixed inset-0 transition-opacity" onClick={() => setShowApprovalModal(false)} data-id="element-9393">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-9394"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-9395">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-9396">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-9397">
                <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4" data-id="element-9398">
                  {approvalAction === 'approve' ? 'Approve Refund' : 'Reject Refund'}
                </h3>
                <div className="space-y-4" data-id="element-9399">
                  <div data-id="element-9400">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-9401">
                      {t('components.RefundModal.notes')}
                    </label>
                    <textarea rows={4} value={approvalNotes} onChange={e => setApprovalNotes(e.target.value)} className="block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500" placeholder={approvalAction === 'approve' ? 'Add any notes about this approval...' : 'Provide a reason for rejection...'} data-id="element-9402" />
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-9403">
                <button type="button" className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${approvalAction === 'approve' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'}`} onClick={handleApproval} data-id="element-9404">
                  {approvalAction === 'approve' ? 'Approve' : 'Reject'}
                </button>
                <button type="button" className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" onClick={() => setShowApprovalModal(false)} data-id="element-9405">
                  {t('components.RefundModal.cancel')}
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}